import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { usePlatformSettings, useUpdatePlatformSettings } from '../hooks/usePlatformSettings'
import type { CompanyInfo, UpdatePlatformSettingsRequest } from '../api/client'
import { Button, Card, CardHeader, Checkbox, Field, Input, PageHeader, Skeleton } from '../components/ui'

function extractApiError(e: unknown): string | undefined {
  return (e as { response?: { data?: { error?: string } } })?.response?.data?.error
}

const emptyCompany: CompanyInfo = {
  'company.legal_name': '',
  'company.trade_name': '',
  'company.address': '',
  'company.city': '',
  'company.phone': '',
  'company.email': '',
  'company.tax_office': '',
  'company.tax_no': '',
  'company.mersis_no': '',
  'company.kep': '',
  'company.website': '',
  'company.legal_email': '',
  'company.hosting_notice': '',
}

// Ordered company field descriptors: [settings key, label i18n suffix].
const companyFields: Array<{ key: keyof CompanyInfo; label: string }> = [
  { key: 'company.legal_name', label: 'legalName' },
  { key: 'company.trade_name', label: 'tradeName' },
  { key: 'company.address', label: 'address' },
  { key: 'company.city', label: 'city' },
  { key: 'company.phone', label: 'phone' },
  { key: 'company.email', label: 'email' },
  { key: 'company.tax_office', label: 'taxOffice' },
  { key: 'company.tax_no', label: 'taxNo' },
  { key: 'company.mersis_no', label: 'mersisNo' },
  { key: 'company.kep', label: 'kep' },
  { key: 'company.website', label: 'website' },
  { key: 'company.legal_email', label: 'legalEmail' },
  { key: 'company.hosting_notice', label: 'hostingNotice' },
]

export default function PlatformSettingsPage() {
  const { t } = useTranslation()
  const { data, isLoading, isError } = usePlatformSettings()
  const update = useUpdatePlatformSettings()

  const [company, setCompany] = useState<CompanyInfo>(emptyCompany)
  const [merchantId, setMerchantId] = useState('')
  const [merchantKey, setMerchantKey] = useState('')
  const [merchantSalt, setMerchantSalt] = useState('')
  const [testMode, setTestMode] = useState(false)
  const [successMsg, setSuccessMsg] = useState('')
  const [errorMsg, setErrorMsg] = useState('')

  // Hydrate local form state from the server payload once loaded. Secret
  // fields stay blank — a blank submit keeps the stored value.
  useEffect(() => {
    if (!data) return
    setCompany({ ...emptyCompany, ...data.company })
    setMerchantId(data.paytr_merchant_id ?? '')
    setTestMode(data.paytr_test_mode)
    setMerchantKey('')
    setMerchantSalt('')
  }, [data])

  const setCompanyField = (key: keyof CompanyInfo, value: string) => {
    setCompany((c) => ({ ...c, [key]: value }))
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setSuccessMsg('')
    setErrorMsg('')
    const payload: UpdatePlatformSettingsRequest = {
      company,
      paytr_merchant_id: merchantId,
      paytr_test_mode: testMode,
    }
    // Only send secrets when the admin typed something — blank keeps stored.
    if (merchantKey.trim() !== '') payload.paytr_merchant_key = merchantKey
    if (merchantSalt.trim() !== '') payload.paytr_merchant_salt = merchantSalt
    try {
      await update.mutateAsync(payload)
      setSuccessMsg(t('platformSettings.saved'))
      setMerchantKey('')
      setMerchantSalt('')
    } catch (err) {
      setErrorMsg(extractApiError(err) ?? t('platformSettings.saveFailed'))
    }
  }

  if (isLoading) {
    return (
      <div className="p-4 sm:p-8 mx-auto w-full max-w-5xl space-y-3">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="p-4 sm:p-8 mx-auto w-full max-w-5xl">
        <p className="text-sm text-red-600">{t('platformSettings.loadFailed')}</p>
      </div>
    )
  }

  const secretPlaceholder = (isSet: boolean) =>
    isSet ? t('platformSettings.secretStored') : t('platformSettings.secretPlaceholder')

  const secretHint = (isSet: boolean) =>
    isSet ? t('platformSettings.secretSetHint') : t('platformSettings.secretUnsetHint')

  return (
    <form onSubmit={handleSave} className="p-4 sm:p-8 mx-auto w-full max-w-5xl space-y-6">
      <PageHeader title={t('platformSettings.title')} description={t('platformSettings.subtitle')} className="mb-0" />

      <Card padded={false} className="p-5 sm:p-6 space-y-4">
        <CardHeader
          title={t('platformSettings.companyCard')}
          description={t('platformSettings.companyHint')}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {companyFields.map((f) => (
            <Field
              key={f.key}
              label={t(`platformSettings.fields.${f.label}`)}
              className={f.key === 'company.address' || f.key === 'company.hosting_notice' ? 'sm:col-span-2' : undefined}
            >
              {(p) => (
                <Input
                  {...p}
                  type="text"
                  value={company[f.key]}
                  onChange={(e) => setCompanyField(f.key, e.target.value)}
                  placeholder={t(`platformSettings.fields.${f.label}`)}
                />
              )}
            </Field>
          ))}
        </div>
      </Card>

      <Card padded={false} className="p-5 sm:p-6 space-y-4">
        <CardHeader
          title={t('platformSettings.paytrCard')}
          description={t('platformSettings.paytrHint')}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t('platformSettings.fields.merchantId')}>
            {(p) => (
              <Input
                {...p}
                type="text"
                value={merchantId}
                onChange={(e) => setMerchantId(e.target.value)}
                placeholder={t('platformSettings.fields.merchantId')}
              />
            )}
          </Field>

          <div className="flex items-end pb-2">
            <Checkbox
              label={t('platformSettings.fields.testMode')}
              checked={testMode}
              onChange={(e) => setTestMode(e.target.checked)}
            />
          </div>

          <Field
            label={t('platformSettings.fields.merchantKey')}
            hint={secretHint(data?.paytr_merchant_key_set ?? false)}
          >
            {(p) => (
              <Input
                {...p}
                type="password"
                autoComplete="new-password"
                value={merchantKey}
                onChange={(e) => setMerchantKey(e.target.value)}
                placeholder={secretPlaceholder(data?.paytr_merchant_key_set ?? false)}
              />
            )}
          </Field>

          <Field
            label={t('platformSettings.fields.merchantSalt')}
            hint={secretHint(data?.paytr_merchant_salt_set ?? false)}
          >
            {(p) => (
              <Input
                {...p}
                type="password"
                autoComplete="new-password"
                value={merchantSalt}
                onChange={(e) => setMerchantSalt(e.target.value)}
                placeholder={secretPlaceholder(data?.paytr_merchant_salt_set ?? false)}
              />
            )}
          </Field>
        </div>
      </Card>

      {successMsg && <p className="text-sm text-green-600">{successMsg}</p>}
      {errorMsg && <p className="text-sm text-red-600">{errorMsg}</p>}

      <div className="flex items-center gap-3">
        <Button type="submit" loading={update.isPending}>
          {update.isPending ? t('platformSettings.saving') : t('platformSettings.save')}
        </Button>
      </div>
    </form>
  )
}
